import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { tracker } from '@unite-us/client-utils';
import { browserHistory } from 'common/utils/browserHistory';
import IndexDetailView from 'src/components/Dashboard/components/IndexDetailView';
import AssistanceRequestsIndex from 'src/components/Dashboard/components/New/AssistanceRequestsIndex';
import { fetchDashboardPendingAssistanceRequests } from 'actions/AssistanceRequest/Group';
import fetchProvidersUserCore from 'src/actions/UserProvider/fetchProvidersUser';
import _ from 'lodash';
import DetailView from 'src/components/Dashboard/components/DetailView';
import AssistanceRequestDetail from 'src/components/AssistanceRequests/components/AssistanceRequestDetail';
import { getDataOfPage, isDataValid } from 'src/components/Dashboard/utils/dataHelpers';
import callOrLog from 'src/common/utils/callOrLog';
import { careCoordinatorsFilter } from 'src/components/Dashboard/utils/filter';
import { noneAssignedOption } from 'src/components/Dashboard/utils/filterDefaults';
import { parentsOnlyServiceTypesFilter } from 'src/components/Dashboard/utils/filter/serviceTypesFilter';
import { DASHBOARD_VIEWS, DASHBOARD_EVENTS } from 'common/utils/EventTracker/utils/eventConstants';
import { getGroupUsersOptions } from 'src/components/Groups/Users/utils';
import labelCustomization from 'src/common/utils/Customization/labels';
import defaultLabels from 'src/constants/labels';
import { loadDashboardSortingConfiguration } from 'src/components/Dashboard/utils/userSortingHandler';
import { createSortChangeHandler } from 'src/components/Dashboard/utils/sortHelpers';
import {
  getInitialDashboardPage,
  refetchCurrentDashboardPage,
  syncDashboardPageFromProps,
} from 'src/components/Dashboard/utils/pageRestore';

export class NewAssistanceRequests extends Component {
  constructor(props) {
    super(props);
    const sortObj = loadDashboardSortingConfiguration(browserHistory.getCurrentLocation().pathname);

    this.state = {
      activeUsersInGroup: [],
      page: getInitialDashboardPage(props, 'assistanceRequests'),
      sidx: sortObj?.column.sortBy || 'created_at',
      sord: sortObj?.sortDirection || 'asc',
      filters: {
        service_types: _.get(props, 'filters.service_types', []),
        care_coordinator_users: _.get(props, 'filters.care_coordinator_users', []),
        start_date: _.get(props, 'filters.start_date', []),
        end_date: _.get(props, 'filters.end_date', []),
      },
    };

    this.fetch = this.fetch.bind(this);
    this.fetchGroupsUsers = this.fetchGroupsUsers.bind(this);
    this.onCellClick = this.onCellClick.bind(this);
    this.onFiltersChange = this.onFiltersChange.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.shouldFetch = this.shouldFetch.bind(this);
    this.onSortChange = this.onSortChange.bind(this);
  }

  componentDidMount() {
    this.fetch(this.state.page);
    this.props.fetchProvidersUserCore({
      providers: this.props.groupId,
    }).then((response) => {
      if (response) {
        const { employees: activeUsersInGroup } = response;
        this.setState({ activeUsersInGroup });
      }
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    syncDashboardPageFromProps(this, nextProps, 'assistanceRequests');

    if (!this.props.refetch && nextProps.refetch) {
      refetchCurrentDashboardPage(this, 'assistanceRequests', nextProps);
    }
  }

  componentDidUpdate(prevProps) {
    if (_.get(prevProps, 'params.id') && !_.get(this.props, 'params.id')) {
      refetchCurrentDashboardPage(this, 'assistanceRequests');
    }
  }

  onSortChange = createSortChangeHandler(this);

  onCellClick(row) {
    const pagedData = getDataOfPage(this.props.assistanceRequests, this.state.page);
    const assistanceRequest = pagedData.data[row] || {};

    callOrLog(() => this.context.eventTracker(DASHBOARD_EVENTS.assistanceRequestsRow, {
      view: DASHBOARD_VIEWS.assistanceRequests,
    }, { assistanceRequest }));

    if (assistanceRequest.id) {
      browserHistory.push({
        pathname: `/dashboard/assistance-requests/new/${assistanceRequest.id}`,
        state: { prevLocation: browserHistory.getCurrentLocation().pathname },
      });
    }
  }

  onFiltersChange(key, activeFilters, callbacks) {
    let newFilters = activeFilters;
    if (_.isEqual(key, 'care_coordinator_users')) {
      newFilters = careCoordinatorsFilter(activeFilters, callbacks);
    }
    this.setState({
      page: 1,
      filters: { ...this.state.filters, ...(key === 'dates' ? activeFilters : { [key]: newFilters }) },
    }, () => {
      callOrLog(() => {
        this.context.eventTracker(
          DASHBOARD_EVENTS.assistanceRequestsFilter,
          tracker.buildDashboardFilterPayload(this.state, this.props),
        );
      });
      this.fetch();
    });
  }

  onNextClick() {
    const page = this.state.page + 1;
    this.fetch(page);
  }

  onPrevClick() {
    const page = this.state.page - 1;
    this.fetch(page);
  }

  shouldFetch(page) {
    // Invalidate current page data to prevent showing wrong records
    if (this.props.assistanceRequests?.data?.[page - 1]) {
      this.props.assistanceRequests.data[page - 1].time = 0;
    }

    if (isDataValid(this.props.assistanceRequests, page)) {
      this.setState({ page });
    } else {
      this.fetch(page);
    }
  }

  fetch(page = 1) {
    const formattedFilters = _.clone(this.state.filters);

    this.setState({ page });
    this.props.fetchDashboardPendingAssistanceRequests(
      this.props.groupId,
      {
        target: 'assistanceRequests',
        sidx: this.state.sidx,
        sord: this.state.sord,
        filters: {
          ...formattedFilters,
          statuses: ['pending'],
        },
        page,
      },
    );
  }

  fetchGroupsUsers(search = '') {
    const { groupId } = this.props;
    return this.props.fetchProvidersUserCore({
      providers: groupId,
      options: {
        text: search,
        licensed: false,
      },
    }).then((response) => {
      if (response) {
        const { employees: activeUsersInGroup } = response;
        if (search === '') {
          return _.concat(
            [noneAssignedOption(this.state)],
            getGroupUsersOptions(activeUsersInGroup, this.state),
          );
        }

        return getGroupUsersOptions(activeUsersInGroup, this.state);
      }

      return [];
    });
  }

  render() {
    const {
      assistanceRequests,
      isFetching,
      params,
      serviceTypes,
      labels,
    } = this.props;
    const { activeUsersInGroup } = this.state;
    const newParams = { ...params, assistance_request_id: params.id };

    let filters = [
      parentsOnlyServiceTypesFilter(serviceTypes, this.state.filters.service_types),
    ];
    filters = _.concat(filters, [
      {
        key: 'care_coordinator_users',
        name: labels.CareCoordinator,
        pluralName: `${labels.CareCoordinator}s`,
        options: _.concat(
          [noneAssignedOption(this.state)],
          getGroupUsersOptions(activeUsersInGroup, this.state),
        ),
      }]);

    const innerContentView = (
      <AssistanceRequestDetail
        params={newParams}
      />
    );

    const prevLocation = browserHistory.getCurrentLocation()?.state?.prevLocation;
    const pathname = _.isNil(prevLocation) ? '/dashboard/assistance-requests/new' : prevLocation;

    const assistanceRequestsDetailView = (
      <DetailView
        type="assistanceRequests"
        params={this.props.params}
        innerContentView={innerContentView}
        hideMessageCenterButton
        onBackClick={() => browserHistory.push(pathname)}
      />
    );
    const pagedData = getDataOfPage(assistanceRequests, this.state.page);
    const sortConfig = {
      sidx: this.state.sidx,
      sord: this.state.sord,
    };

    return (
      <IndexDetailView
        params={this.props.params}
        DetailView={assistanceRequestsDetailView}
        IndexView={AssistanceRequestsIndex}
        data={pagedData.data}
        paging={pagedData.paging}
        isFetching={isFetching || pagedData.invalidated}
        onNextClick={this.onNextClick}
        onPrevClick={this.onPrevClick}
        onCellClick={this.onCellClick}
        initialSort={{ [this.state.sidx]: this.state.sord }}
        onFiltersChange={this.onFiltersChange}
        filters={_.sortBy(filters, 'name')}
        fetchGroupsUsers={this.fetchGroupsUsers}
        addDateRangeFilter
        sortConfig={sortConfig}
        setSort={this.onSortChange}
      />
    );
  }
}

NewAssistanceRequests.propTypes = {
  assistanceRequests: PropTypes.shape({
    data: PropTypes.array,
    currentPage: PropTypes.number,
  }).isRequired,
  fetchDashboardPendingAssistanceRequests: PropTypes.func.isRequired,
  fetchProvidersUserCore: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
  isFetching: PropTypes.bool.isRequired,
  params: PropTypes.object.isRequired,
  refetch: PropTypes.bool.isRequired,
  serviceTypes: PropTypes.array.isRequired,
  labels: PropTypes.object,
};

NewAssistanceRequests.defaultProps = {
  labels: defaultLabels,
};

function mapStateToProps(state) {
  const isCC = _.wget(state, 'session.isCoordinationGroup', false);

  return {
    assistanceRequests: _.get(state, 'dashboard.assistanceRequests', {}),
    filters: _.get(state, 'dashboard.assistanceRequests.filters', {}),
    groupId: _.get(state, 'session.groupId'),
    isFetching: _.get(state, 'dashboard.assistanceRequests.isFetching', false),
    refetch: _.get(state, 'dashboard.refetch'),
    serviceTypes: _.wget(state, isCC ? 'serviceTypes.network' : 'serviceTypes.allPrograms', []),
    token: _.get(state, 'session.token', ''),
    userId: _.get(state, 'user.id'),
    labels: labelCustomization(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    ...bindActionCreators({ fetchDashboardPendingAssistanceRequests, fetchProvidersUserCore }, dispatch),
    dispatch,
  };
}

NewAssistanceRequests.contextTypes = {
  eventTracker: PropTypes.func.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(NewAssistanceRequests);
